<template>
  <div v-click-outside="() => handleUnselect()" :class="['border w-[168px] h-[207px] bg-white rounded', {'border-[#c2c4c6]': !isSelected, 'border-[#2A8176]': isSelected}]">
    <div class="pdf-preview w-full pt-2.5 px-1.5">
      <img
        :src="
          preview?.preview
            ? preview.preview
            : isCoverLetter ? '/img/pdf-preview-coverletter.png' : '/img/pdf-preview.png'
        "
        @error="handleErrorLoad"
      />

      <div :class="['pdf-preview-action hidden', {'cursor-pointer': hideActions }]" @click="() => handleSelect(preview)">
        <won-button v-if="!hideActions" @click="handleEdit">
          <DownloadIcon color="white" v-if="preview.keep_original_format"/>
          <edit color="white" v-else/>
          
          <span v-if="preview.keep_original_format">
            Download
          </span>
          <span v-else>
            Edit
          </span>
        </won-button>
      </div>
    </div>

    <div class="pdf-labels">
      <div
        class="
          grid grid-cols-2
          content-center
          justify-center
          items-center
          w-full
        "
        style="grid-template-columns: 1fr 16px"
      >
        <input
          v-if="rename"
          v-model="renameValue"
          ref="renameEl"
          class="
            won-blue
            text-sm
            font-medium
            px-0
            py-0
            w-full
            focus:outline-none
          "
          @blur="handleEndRename"
          @keypress.enter="handleEndRename"
        />

        <div
          v-else
          class="
            won-blue
            text-sm
            font-medium
            text-ellipsis
            whitespace-nowrap
            overflow-hidden
            w-full
          "
        >
          {{ extractContent(preview.title) || 'Title' }}
        </div>
        <preview-action
          v-if="!hideActions"
          :isWonFormat="!preview?.keep_original_format"
          :isPrimary="isPrimaryResume"
          :isCoverLetter="isCoverLetter"
          @preview-actions:tailor="() => handleTailorResume()"
          @preview-actions:downloadpdf="() => handleDownload(DOCUMENT_PDF)"
          @preview-actions:downloaddocx="() => handleDownload(DOCUMENT_DOCX)"
          @preview-actions:rename="handleRename"
          @preview-actions:delete="handleDelete"
          @preview-actions:duplicate="duplicateDispatcher"
          @preview-actions:set-primary="setPrimaryResumeHandler"
          @preview-actions:set-new-primary="primaryResumeStore.setShowSelectPrimaryResume"
        />
        <div v-else class="cursor-pointer" @click="handleOpenPreview">
          <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">            <g clip-path="url(#clip0_1670_50940)">            <path d="M14.7499 14.25H4.24994V3.75H9.49994V2.25H4.24994C3.41744 2.25 2.74994 2.925 2.74994 3.75V14.25C2.74994 15.075 3.41744 15.75 4.24994 15.75H14.7499C15.5749 15.75 16.2499 15.075 16.2499 14.25V9H14.7499V14.25ZM10.9999 2.25V3.75H13.6924L6.31994 11.1225L7.37744 12.18L14.7499 4.8075V7.5H16.2499V2.25H10.9999Z" fill="#8897AA"/>            </g>            <defs>            <clipPath id="clip0_1670_50940">            <rect width="18" height="18" fill="white" transform="translate(0.500061)"/>            </clipPath>            </defs>            </svg>
        </div>
      </div>

      <div class="mt-1 won-label-gray text-xs">
        {{ docCreationDate }}
      </div>
    </div>
  </div>

  <delete-modal
    :key="deleteVisible"
    :visible="deleteVisible"
    :title="`Delete \`${extractContent(preview.title) || 'Title'}\``"
    :loading="deleteLoading"
    subtitle="Are you sure you want to delete this resume?"
    @delete-modal:close="handleCloseDelete"
    @delete-modal:confirm="handleDeleteConfirm"
  />
</template>

<script>
import { ref, toRefs, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from "pinia"
import { extractContent } from '../../utils/helpers'
import { useDateFormat } from "@vueuse/core";

import { DOCUMENT_DOCX, DOCUMENT_PDF } from '../../store/builder-style.store'
import { DEFAULT_DATE_FORMAT } from "../../utils/constants"

import { useBuilder } from '../../store/builder.store'
import { useResumesStore } from "../../store/resumes.store";
import { useCoverLettersStore } from "../../store/cover-letters.store";
import { usePrimaryResumeStore } from "../../store/primary-resume.store";
import { useNotifications } from "../../store/notifications.store";

import {
    retrieveResume,
    updateResume,
} from '../../api/builder'

import {
    createCoverLetter,
    retrieveCoverLetter,
    updateCoverLetter
} from '../../api/cover-letters'

import PreviewAction from './preview-action.vue'
import WonButton from '../won-button.vue'
import Edit from '../../icons/actions/edit.vue'
import DeleteModal from '../../modals/delete-modal.vue'

import DownloadIcon from "../../icons/download-resume-icon.vue"

export default {
  components: {
      PreviewAction,
      WonButton,
      Edit,
      DeleteModal,
      DownloadIcon
  },

  props: {
    isCoverLetter: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    preview: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['pdf-preview:refresh',  'pdf-preview:tailor', 'selected'],

  setup(props) {
    const { isCoverLetter, hideActions, selected, preview } = toRefs(props)

    const renameEl = ref(null)
    const rename = ref(false)
    const renameValue = ref('')

    const isSelected = ref(selected.value)

    const deleteVisible = ref(false)
    const deleteLoading = ref(false)

    const router = useRouter()
    const builder = useBuilder()
    const notificationsStore = useNotifications();
    const resumesStore = useResumesStore();
    const coverLetterStore = useCoverLettersStore();
    builder.activeRoute = useRoute()
      
    const docCreationDate = useDateFormat(preview.value.created_at, DEFAULT_DATE_FORMAT)

    const primaryResumeStore = usePrimaryResumeStore();
    const { primaryResume } = storeToRefs(primaryResumeStore);

    watch(() => selected.value, newval => {
      isSelected.value = newval
    })

    return {
      hideActions,
      isSelected,
      isCoverLetter,
      builder,
      resumesStore,
      coverLetterStore,
      primaryResumeStore,
      primaryResume,

      rename,
      renameValue,
      renameEl,
      docCreationDate,

      deleteLoading,
      deleteVisible,

      DOCUMENT_DOCX, DOCUMENT_PDF,

      router,
      navigate: router.push,
      extractContent
    }
  },
    
  computed: {
    isPrimaryResume() {
        return this.preview?.uuid === this.primaryResume?.uuid
    }
  },

  methods: {
    handleUnselect() {
      if(!this.hideActions) return
      this.isSelected=false
    },
    handleSelect(item) {
      if(!this.hideActions) return
      this.isSelected=true
      this.$emit('selected', item)
    },
    handleOpenPreview() {
      const routeData = this.router.resolve({name: this.isCoverLetter? 'cover-letters.edit' : 'resumes.edit', params: {id: this.preview.uuid}});
      window.open(routeData.href, '_blank');
    },
    handleErrorLoad(event) {
      event.target.src = this.isCoverLetter ? '/img/pdf-preview-coverletter.png' : '/img/pdf-preview.png'
    },

    handleRename() {
      this.renameValue = extractContent(this.preview.title)
      this.rename = true

      setTimeout(() => {
        this.renameEl.focus()
      }, 10)
    },

    setPrimaryResumeHandler() {
        this.primaryResumeStore.setPrimaryResume(this.preview.uuid)
    },

    async handleEndRename() {
      this.rename = false
      this.preview.title = this.renameValue

      const resume = this.isCoverLetter ? await retrieveCoverLetter(this.preview.uuid) : await retrieveResume(this.preview.uuid)

      this.isCoverLetter 
        ? updateCoverLetter(this.preview.uuid, { ...resume, title: this.preview.title })
        : updateResume(this.preview.uuid, { ...resume, title: this.preview.title })
    },

    handleDelete() {
      this.deleteVisible = true
    },

    handleCloseDelete() {
      this.deleteVisible = false
    },

    async handleDeleteConfirm() {
      this.deleteLoading = true

      try {
        if(this.isCoverLetter) {
          await this.coverLetterStore.deleteCoverLetter(this.preview.uuid);
        }else{
          await this.resumesStore.deleteResume(this.preview.uuid);
        }
      }catch (e) {
          this.notificationsStore.notify(`Failed to delete ${this.isCoverLetter ? 'cover letter' : 'resume'}!`, 'error')
      }finally {
        this.handleCloseDelete();
        this.deleteLoading = false;
      }
    },

    handleEdit() {
      if(this.preview.keep_original_format) {
        const link = document.createElement('a');
        link.href = this.preview.meta.mediaLink;
        document.body.appendChild(link);
        link.click();

        return
      }else{
        this.navigate({
          name: this.isCoverLetter ? 'cover-letters.edit' : 'resumes.edit' ,
          params: { id: this.preview.uuid },
        })
      }
    },
    
    duplicateDispatcher() {
      if(this.isCoverLetter){
        this.duplicateCoverLetterHandler();
      }else{
        this.duplicateResumeHandler()
      }
    },
      
    async duplicateResumeHandler() {
      const resumeId = this.preview.uuid;
      
      await this.resumesStore.duplicateResume(resumeId);
    },

    async duplicateCoverLetterHandler() {
      const result = await retrieveCoverLetter(this.preview.uuid);
      const parsed = this.builder.buildData(result)

      const data = {
        ...parsed,
        title: parsed.title + ' - Copy',
        id: undefined,
      }

      await createCoverLetter(data);
      this.$emit('pdf-preview:refresh')
    },

    handleDownload(format) {
      this.builder.download(this.preview.uuid, format)
    },
    
    handleTailorResume() {
      this.$emit('pdf-preview:tailor')
    }
  },
}
</script>

<style scoped>
.pdf-preview {
  overflow: hidden;
  height: 139px;
  position: relative;
}

.pdf-preview:hover > .pdf-preview-action {
  display: flex;
}

.pdf-preview-action {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  justify-content: center;
  align-items: center;
}

.pdf-preview-action button {
  background-color: #505d68;
  display: flex;
  align-items: center;
  height: 22px;
  font-size: 12px;
}

.pdf-preview-action button svg {
  width: 14px;
  height: 14px;
  margin-right: 4px;
}

.pdf-preview > img {
  width: 100%;
  object-position: top;
}

.pdf-labels {
  padding: 13px 8px 13px 15px;
}
</style>
